<template>
    <div class="vue-datatable ppv2-bg-white dark:ppv2-bg-dark">
        <!-- Search Input -->
        <div class="datatable-header">
            <div class="header-left">
                <slot name="header-left"></slot>
                <!-- Column Toggle Button -->
                <div v-if="showColumnToggleButton" class="column-toggle-container">
                    <button
                        @click="toggleColumnSelector"
                        class="btn fs-sm btn-dark px-3 rounded-8 fw-medium columns-list"
                        type="button"
                    >
                        <i class="fas fa-table"></i>
                    </button>
                    <!-- Column Selector Dropdown -->
                    <div v-if="showColumnSelector" class="column-selector-dropdown">
                        <div class="column-selector-header">
                            <h6>Colonnes visibles</h6>
                            <button @click="closeColumnSelector" class="column-selector-close-btn" type="button">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M18 6L6 18"
                                        stroke="#7B8289"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <path
                                        d="M6 6L18 18"
                                        stroke="#7B8289"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </button>
                        </div>
                        <div class="column-selector-body">
                            <div v-for="column in allColumns" :key="column.key" class="column-toggle-item">
                                <label class="column-toggle-label">
                                    <input
                                        type="checkbox"
                                        :checked="!pendingHiddenColumns.includes(column.key)"
                                        @change="togglePendingColumnVisibility(column.key)"
                                        class="column-toggle-checkbox"
                                    />
                                    <span class="column-toggle-text">{{ column.title }}</span>
                                </label>
                            </div>
                        </div>
                        <div class="column-selector-footer">
                            <button
                                @click="applyColumnChanges"
                                class="btn fs-sm btn-dark py-2 rounded-8 fw-medium ms-2 mt-4 btn-validate btn-sm w-100"
                            >
                                Valider
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="header-right">
                <slot name="header-right"></slot>
                <div class="search-container">
                    <svg
                        width="19"
                        height="19"
                        viewBox="0 0 19 19"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="search-icon"
                    >
                        <path
                            d="M8.74996 15.8327C12.662 15.8327 15.8333 12.6614 15.8333 8.74935C15.8333 4.83727 12.662 1.66602 8.74996 1.66602C4.83788 1.66602 1.66663 4.83727 1.66663 8.74935C1.66663 12.6614 4.83788 15.8327 8.74996 15.8327Z"
                            stroke="#7B8289"
                            stroke-width="1.5"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M11.107 5.97625C10.7978 5.66627 10.4304 5.42044 10.0259 5.2529C9.62136 5.08536 9.18774 4.99942 8.74991 5C8.31208 4.99942 7.87845 5.08536 7.47394 5.2529C7.06943 5.42044 6.70202 5.66627 6.39282 5.97625M13.8424 13.8425L17.3778 17.3779"
                            stroke="#7B8289"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                    <input
                        v-model="searchQuery"
                        @input="onSearch"
                        type="text"
                        :placeholder="$t('datatable.search')"
                        class="search-input"
                    />
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-container">
            <div class="loading-spinner"></div>
            <span>{{ $t('datatable.loading') }}</span>
        </div>

        <!-- Data Table -->
        <div v-else class="table-container">
            <table class="datatable">
                <thead>
                    <tr>
                        <th v-if="showSelection">
                            <input
                                type="checkbox"
                                @change="selectAll"
                                :checked="selectedItems.length === data.length && data.length > 0"
                            />
                        </th>
                        <th
                            v-for="column in visibleColumns"
                            :key="column.key"
                            @click="column.sortable !== false && onSort(column.key)"
                            class=""
                            :class="{ sortable: column.sortable !== false }"
                        >
                            <div class="column-header">
                                <span>{{ column.title }}</span>
                                <span v-if="column.sortable !== false" class="sort-icon">
                                    <i
                                        :class="{
                                            'sort-asc': sortKey === column.key && sortOrder === 'asc',
                                            'sort-desc': sortKey === column.key && sortOrder === 'desc',
                                            'sort-none': sortKey !== column.key,
                                        }"
                                    ></i>
                                </span>
                            </div>
                            <div v-if="column.search" class="column-search" @click.stop>
                                <input
                                    v-if="column.search.type === 'text'"
                                    v-model="columnSearches[column.key]"
                                    @input="onColumnSearch(column.key)"
                                    :placeholder="'Rechercher ' + column.title"
                                    type="text"
                                    class="column-search-input"
                                />
                                <select
                                    v-else-if="column.search.type === 'select'"
                                    v-model="columnSearches[column.key]"
                                    @change="onColumnSearch(column.key)"
                                    class="column-search-select"
                                >
                                    <option value="" disabled :selected="!columnSearches[column.key]">
                                        {{ column.search.placeholder || 'Sélectionner' }}
                                    </option>
                                    <option v-for="option in column.search.options" :key="option.value" :value="option.value">
                                        {{ option.label }}
                                    </option>
                                </select>
                                <input
                                    v-else-if="column.search.type === 'date'"
                                    v-model="columnSearches[column.key]"
                                    @input="onColumnSearch(column.key)"
                                    :placeholder="'Sélectionner une date'"
                                    type="date"
                                    class="column-search-input"
                                />
                                <input
                                    v-else-if="column.search.type === 'number'"
                                    v-model="columnSearches[column.key]"
                                    @input="onColumnSearch(column.key)"
                                    :placeholder="'Entrer un nombre'"
                                    type="number"
                                    class="column-search-input"
                                />
                                <div v-else-if="column.search.type === 'date_range'" class="date-range-container">
                                    <input
                                        v-model="columnSearches[column.key + '_from']"
                                        @input="onColumnSearch(column.key)"
                                        placeholder="Du"
                                        type="date"
                                        class="column-search-input date-range-input"
                                    />
                                    <input
                                        v-model="columnSearches[column.key + '_to']"
                                        @input="onColumnSearch(column.key)"
                                        placeholder="Au"
                                        type="date"
                                        class="column-search-input date-range-input"
                                    />
                                </div>
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="data.length === 0">
                        <td :colspan="visibleColumns.length + (showSelection ? 1 : 0)" class="no-data">
                            {{ $t('datatable.noData') }}
                        </td>
                    </tr>
                    <tr
                        v-for="(item, index) in data"
                        :key="index"
                        @click="onRowClick(item)"
                        :class="{ 'datatable-row-odd': index % 2 !== 0 }"
                    >
                        <td v-if="showSelection">
                            <input type="checkbox" :value="item" v-model="selectedItems" @change="onSelectionChange" />
                        </td>
                        <td v-for="column in visibleColumns" :key="column.key">
                            <slot
                                v-if="column.slot"
                                :name="column.slot"
                                :item="item"
                                :value="getNestedValue(item, column.key)"
                            ></slot>
                            <span v-else>{{ formatValue(item, column) }}</span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Table Info and Pagination -->
        <div class="datatable-footer">
            <div class="footer-left">
                <VDropdown
                    v-if="showBulkActions"
                    :disabled="selectedItems.length === 0"
                    :label="'Actions groupées' + (selectedItems.length > 0 ? ' (' + selectedItems.length + ')' : '')"
                    align="right"
                    position="top"
                    :items="exportItems"
                    @select="handleDropDown"
                />
            </div>
            <div class="pagination-container">
                <button @click="goToPage(1)" :disabled="currentPage === 1" class="pagination-btn" :title="$t('datatable.first')">
                    <i class="fas fa-angle-double-left"></i>
                </button>
                <button
                    @click="goToPage(currentPage - 1)"
                    :disabled="currentPage === 1"
                    class="pagination-btn"
                    :title="$t('datatable.previous')"
                >
                    <i class="fas fa-angle-left"></i>
                </button>

                <span class="pagination-numbers">
                    <button
                        v-for="page in visiblePages"
                        :key="page"
                        @click="goToPage(page)"
                        :class="{ active: currentPage === page }"
                        class="pagination-btn"
                    >
                        {{ page }}
                    </button>
                </span>

                <button
                    @click="goToPage(currentPage + 1)"
                    :disabled="currentPage === totalPages"
                    class="pagination-btn"
                    :title="$t('datatable.next')"
                >
                    <i class="fas fa-angle-right"></i>
                </button>
                <button
                    @click="goToPage(totalPages)"
                    :disabled="currentPage === totalPages"
                    class="pagination-btn"
                    :title="$t('datatable.last')"
                >
                    <i class="fas fa-angle-double-right"></i>
                </button>
            </div>
            <div class="footer-right">
                <label>{{ $t('datatable.show') }}</label>
                <select v-model="perPage" @change="onPerPageChange" class="per-page-select">
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <!-- <option value="200">200</option>
                    <option value="500">500</option>
                    <option value="-1">All</option> -->
                </select>
                <label>{{ $t('datatable.entries') }}</label>
            </div>
        </div>

        <!-- Delete Modal -->
        <div v-if="showDeleteModal" class="modal-overlay">
            <div class="modal-content">
                <button @click="closeDeleteModal" class="close-button">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18" stroke="#7B8289" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M6 6L18 18" stroke="#7B8289" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </button>
                <h3 class="block-title fs-5 poppinsSemiBold">Confirmer la suppression</h3>
                <p>Êtes-vous sûr de vouloir supprimer les tables sélectionnées ?</p>
                <div class="modal-actions">
                    <button
                        class="btn fs-sm btn-outline-dark py-2 mx-2 rounded-12 fw-medium width-btn"
                        customClass="!ppv2-h-[45px]"
                        @click="closeDeleteModal"
                    >
                        Annuler
                    </button>
                    <button
                        class="btn fs-sm btn-danger py-2 rounded-12 fw-medium width-btn"
                        customClass="!ppv2-h-[45px]"
                        @click="confirmDelete"
                    >
                        Supprimer
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import VDropdown from './VDropdown.vue'

export default {
    name: 'VueDataTable',
    components: {
        VDropdown,
    },
    props: {
        columns: {
            type: Array,
            required: true,
            // Expected format: [{ key: 'name', title: 'Name', sortable: true, slot: 'name', search: { type: 'text' } or { type: 'select', options: [{value: 'val', label: 'Label'}] } or { type: 'date' } or { type: 'number' } or { type: 'date_range' } }]
        },
        apiUrl: {
            type: String,
            required: true,
        },
        apiMethod: {
            type: String,
            default: 'GET',
        },
        apiHeaders: {
            type: Object,
            default: () => ({}),
        },
        apiParams: {
            type: Object,
            default: () => ({}),
        },
        serverSide: {
            type: Boolean,
            default: true,
        },
        autoLoad: {
            type: Boolean,
            default: true,
        },
        noDataText: {
            type: String,
            default() {
                return this.$t('datatable.noData')
            },
        },
        searchDebounce: {
            type: Number,
            default: 500,
        },
        showSelection: {
            type: Boolean,
            default: true,
        },
        showBulkActions: {
            type: Boolean,
            default: true,
        },
        showColumnToggleButton: {
            type: Boolean,
            default: false,
        },
        localStorageKey: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            data: [],
            loading: false,
            currentPage: 1,
            perPage: 10,
            totalEntries: 0,
            totalUnfilteredEntries: 0,
            sortKey: '',
            sortOrder: 'asc',
            searchQuery: '',
            searchTimeout: null,
            selectedItems: [],
            isDeleting: false,
            showDeleteModal: false,
            showColumnSelector: false,
            hiddenColumns: [],
            pendingHiddenColumns: [],
            columnSearches: {},

            exportItems: [{ label: 'Supprimer la sélection', value: 'delete' }],
            perPageOptions: [
                { value: 5, label: '5' },
                { value: 10, label: '10' },
                { value: 25, label: '25' },
                { value: 50, label: '50' },
                { value: 100, label: '100' },
                { value: 200, label: '200' },
                { value: 500, label: '500' },
                { value: -1, label: 'All' },
            ],
        }
    },
    computed: {
        totalPages() {
            return Math.ceil(this.totalEntries / this.perPage)
        },
        startEntry() {
            return this.totalEntries === 0 ? 0 : (this.currentPage - 1) * this.perPage + 1
        },
        endEntry() {
            const end = this.currentPage * this.perPage
            return end > this.totalEntries ? this.totalEntries : end
        },
        visiblePages() {
            const pages = []
            const start = Math.max(1, this.currentPage - 2)
            const end = Math.min(this.totalPages, this.currentPage + 2)

            for (let i = start; i <= end; i++) {
                pages.push(i)
            }

            return pages
        },
        allColumns() {
            return this.columns
        },
        visibleColumns() {
            return this.columns.filter((col) => !this.hiddenColumns.includes(col.key))
        },
    },
    mounted() {
        this.loadColumnVisibilityFromStorage()
        this.initializeHiddenColumns()
        if (this.autoLoad) {
            this.fetchData()
        }
    },
    methods: {
        async fetchData() {
            this.loading = true

            try {
                const params = {
                    ...this.apiParams,
                    page: this.currentPage,
                    per_page: this.perPage,
                    search: this.searchQuery,
                    sort_by: this.sortKey,
                    sort_order: this.sortOrder,
                    ...this.columnSearches,
                }

                const response = await this.makeApiRequest(params)
                this.handleApiResponse(response)
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.$emit('error', error)
            } finally {
                this.loading = false
            }
        },

        async makeApiRequest(params) {
            const config = {
                method: this.apiMethod,
                headers: {
                    'Content-Type': 'application/json',
                    ...this.apiHeaders,
                },
            }

            let url = this.apiUrl

            if (this.apiMethod === 'GET') {
                const queryString = new URLSearchParams(params).toString()
                url += (url.includes('?') ? '&' : '?') + queryString
            } else {
                config.body = JSON.stringify(params)
            }

            const response = await fetch(url, config)

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            return await response.json()
        },

        handleApiResponse(response) {
            let _response = response.data
            if (_response.data && Array.isArray(_response.data)) {
                // Laravel-style pagination
                this.data = _response.data
                this.totalEntries = _response.total || _response.data.length
                this.totalUnfilteredEntries = _response.total || _response.data.length
            } else if (_response.items && Array.isArray(_response.items)) {
                // Custom format with items
                this.data = _response.items
                this.totalEntries = _response.total || _response.items.length
                this.totalUnfilteredEntries = _response.totalUnfiltered || _response.total || _response.items.length
            } else if (Array.isArray(_response)) {
                // Simple array response
                this.data = _response
                this.totalEntries = _response.length
                this.totalUnfilteredEntries = _response.length
            } else {
                // Fallback
                this.data = []
                this.totalEntries = 0
                this.totalUnfilteredEntries = 0
            }

            this.$emit('data-loaded', {
                data: this.data,
                total: this.totalEntries,
                page: this.currentPage,
                perPage: this.perPage,
            })
        },

        onSearch() {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.currentPage = 1
                this.fetchData()
            }, this.searchDebounce)
        },

        onPerPageChange() {
            this.currentPage = 1
            this.fetchData()
        },

        onSort(key) {
            if (this.sortKey === key) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortKey = key
                this.sortOrder = 'asc'
            }
            this.currentPage = 1
            this.fetchData()
        },

        goToPage(page) {
            if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
                this.currentPage = page
                this.fetchData()
            }
        },

        refresh() {
            this.fetchData()
        },

        reset() {
            this.currentPage = 1
            this.searchQuery = ''
            this.sortKey = ''
            this.sortOrder = 'asc'
            this.columnSearches = {}
            this.fetchData()
        },

        clearSelection() {
            this.selectedItems = []
            this.onSelectionChange()
        },

        onRowClick(item) {
            this.$emit('row-click', item)
        },

        getNestedValue(obj, path) {
            return path.split('.').reduce((current, key) => current && current[key], obj)
        },

        formatValue(item, column) {
            const value = this.getNestedValue(item, column.key)

            if (column.formatter && typeof column.formatter === 'function') {
                return column.formatter(value, item)
            }

            return value
        },

        selectAll(event) {
            if (event.target.checked) {
                this.selectedItems = [...this.data]
            } else {
                this.selectedItems = []
            }
            this.onSelectionChange()
        },

        onSelectionChange() {
            this.$emit('selection-changed', this.selectedItems)
        },

        deleteSelected() {
            this.showDeleteModal = true
        },

        confirmDelete() {
            this.$emit('delete-selected', this.selectedItems)
            this.closeDeleteModal()
        },

        closeDeleteModal() {
            this.showDeleteModal = false
        },

        closeColumnSelector() {
            this.showColumnSelector = false
        },

        handleDropDown({ value, item }) {
            if (value === 'delete') this.showDeleteModal = true
        },

        toggleColumnSelector() {
            // Initialize hiddenColumns based on column visibility property if not already done
            if (this.hiddenColumns.length === 0) {
                this.initializeHiddenColumns()
            }
            this.pendingHiddenColumns = [...this.hiddenColumns]
            this.showColumnSelector = !this.showColumnSelector
        },

        isColumnVisible(columnKey) {
            return !this.hiddenColumns.includes(columnKey)
        },

        togglePendingColumnVisibility(columnKey) {
            if (this.pendingHiddenColumns.includes(columnKey)) {
                this.pendingHiddenColumns = this.pendingHiddenColumns.filter((key) => key !== columnKey)
            } else {
                this.pendingHiddenColumns.push(columnKey)
            }
            // Update localStorage immediately when user toggles columns
            this.saveColumnVisibilityToStorage()
        },

        initializeHiddenColumns() {
            // Initialize hiddenColumns based on the visible property of columns
            // If visible is not defined, default to true (visible)
            this.hiddenColumns = this.columns.filter((col) => col.visible === false).map((col) => col.key)
        },

        applyColumnChanges() {
            // Apply the pending changes to the actual hidden columns
            this.hiddenColumns = [...this.pendingHiddenColumns]
            // Save to localStorage
            this.saveColumnVisibilityToStorage()
            // Close the dropdown after applying changes
            this.showColumnSelector = false
            this.$forceUpdate()
        },

        loadColumnVisibilityFromStorage() {
            if (!this.localStorageKey) return

            try {
                const stored = localStorage.getItem(`datatable_columns_${this.localStorageKey}`)
                if (stored) {
                    const parsed = JSON.parse(stored)
                    if (Array.isArray(parsed) && parsed.length > 0) {
                        // parsed contains visible column keys
                        const allColumnKeys = this.columns.map((col) => col.key)
                        // Hide columns that are not in the stored visible list
                        this.hiddenColumns = allColumnKeys.filter((key) => !parsed.includes(key))
                    }
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.warn('Failed to load column visibility from localStorage:', error)
            }
        },

        saveColumnVisibilityToStorage() {
            if (!this.localStorageKey) return

            try {
                // Store the visible column keys
                const visibleColumnKeys = this.columns
                    .filter((col) => !this.hiddenColumns.includes(col.key))
                    .map((col) => col.key)

                if (visibleColumnKeys.length > 0) {
                    localStorage.setItem(`datatable_columns_${this.localStorageKey}`, JSON.stringify(visibleColumnKeys))
                } else {
                    // If no columns are visible (edge case), remove the key
                    localStorage.removeItem(`datatable_columns_${this.localStorageKey}`)
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.warn('Failed to save column visibility to localStorage:', error)
            }
        },

        onColumnSearch(columnKey) {
            const column = this.columns.find((col) => col.key === columnKey)
            const searchValue = this.columnSearches[columnKey]

            // For text searches, only run if length >= 2
            if (column.search.type === 'text' && searchValue && searchValue.length < 2) {
                return
            }

            // For select, date, number, date_range searches, run immediately
            if (
                column.search.type === 'select' ||
                column.search.type === 'date' ||
                column.search.type === 'number' ||
                column.search.type === 'date_range'
            ) {
                this.currentPage = 1
                this.fetchData()
                return
            }

            // For text searches, debounce
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.currentPage = 1
                this.fetchData()
            }, this.searchDebounce)
        },
    },
}
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    width: 500px;
    position: relative;
}

.close-button {
    position: absolute;
    top: 10px;
    right: 10px;
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
}

.modal-content h2 {
    margin-top: 0;
}

.modal-content input {
    width: 100%;
    padding: 8px;
    margin-bottom: 10px;
}

.modal-content button {
    margin-right: 10px;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;
}

.datatable-header {
    border-bottom: none;
}

.header-left {
    flex: 1;
    max-width: 300px;
}

.search-container {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-end;
}

.search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    z-index: 1;
}

.search-input {
    width: 40px;
    height: 40px;
    padding-left: 40px;
    border: 1px solid #ced4da;
    border-radius: 11px;
    font-size: clamp(12px, 0.729545vw, 16px);
    outline: none;
    transition: width 0.4s ease-in-out;
    cursor: pointer;
}

.search-input::placeholder {
    color: transparent;
    transition: color 0.4s ease-in-out;
}

.search-input:focus {
    width: 250px;
    cursor: text;
    border-color: #7d2dbd;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.search-input:focus::placeholder {
    color: #6c757d;
}

.header-right {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: clamp(12px, 0.729545vw, 16px);
    color: #6c757d;
}

.per-page-select {
    padding: 6px 8px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: clamp(12px, 0.729545vw, 16px);
    text-align: center;
}

.loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px;
    gap: 12px;
    color: #6c757d;
}

.loading-spinner {
    width: 20px;
    height: 20px;
    border: 2px solid #f3f3f3;
    border-top: 2px solid #7d2dbd;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.table-container {
    border-radius: 20px;
    border: 1px solid rgba(123, 130, 137, 0.3);
    box-shadow: 0px 4px 40px 0px rgba(0, 0, 0, 0.05);
    overflow-x: auto;
}

.datatable {
    width: 100%;
    border-collapse: collapse;
}

.datatable th,
.datatable td {
    padding: 12px;
    text-align: left;
    /* border-bottom: 1px solid #dee2e6; */
}

.datatable th.sortable {
    cursor: pointer;
    user-select: none;
}

/* .datatable th.sortable:hover {
    background-color: #e9ecef;
} */

.datatable tr {
    color: #7b8289;
}

.sort-icon {
    margin-left: 8px;
    opacity: 0.5;
}

.sort-icon i {
    display: inline-block;
    width: 0;
    height: 0;
}

.datatable tbody tr.datatable-row-odd {
    background: #f8fafc !important;
}

.no-data {
    text-align: center;
    color: #6c757d;
    font-style: italic;
    padding: 40px;
}

.pagination-container {
    display: flex;
    justify-content: center;
    flex-grow: 1;
    align-items: center;
    gap: 4px;
}

.pagination-btn {
    padding: 8px 12px;
    /* border: 1px solid #dee2e6; */
    background: white;
    color: #7d2dbd;
    cursor: pointer;
    border-radius: 20px;
    font-size: clamp(12px, 0.729545vw, 16px);
    transition: all 0.2s;
}

.pagination-numbers {
    display: flex;
    gap: 2px;
}

.page-number {
    min-width: 36px;
    text-align: center;
}

.column-toggle-container {
    position: relative;
}

.column-selector-dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    background: white;
    border: 1px solid #ced4da;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    z-index: 1050;
    min-width: 250px;
    max-width: 300px;
    margin-top: 5px;
}

.column-selector-header {
    padding: 12px 16px;
    border-bottom: 1px solid #e9ecef;
    margin-bottom: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.column-selector-header h6 {
    margin: 0;
    font-size: clamp(12px, 0.729545vw, 16px);
    font-weight: 600;
    color: #495057;
}

.column-selector-close-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    color: #6c757d;
    transition: background-color 0.2s;
}

.column-selector-close-btn:hover {
    background-color: #f8f9fa;
}

.column-selector-body {
    padding: 8px 0;
    max-height: 300px;
    overflow-y: auto;
}

.column-toggle-item {
    padding: 8px 16px;
}

.column-toggle-item:hover {
    background-color: #f8f9fa;
}

.column-toggle-label {
    display: flex;
    align-items: center;
    cursor: pointer;
    margin: 0;
    width: 100%;
}

.column-toggle-checkbox {
    margin-right: 12px;
    cursor: pointer;
}

.column-toggle-text {
    font-size: clamp(12px, 0.729545vw, 16px);
    color: #495057;
    user-select: none;
}

.column-selector-footer {
    padding: 12px 16px;
    border-top: 1px solid #e9ecef;
    text-align: center;
    display: flex;
    justify-content: center;
}

.column-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.column-search {
    margin-top: 8px;
}

.column-search-input {
    width: 100%;
    padding: 4px 8px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 12px;
}

.column-search-select {
    width: 100%;
    padding: 4px 8px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 12px;
}

.date-range-container {
    display: flex;
    gap: 4px;
}

.date-range-input {
    flex: 1;
}

@media (max-width: 768px) {
    .header-left {
        max-width: none;
    }

    .pagination-container {
        justify-content: center;
        flex-wrap: wrap;
    }
}
</style>
